// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Mirrors the old Flutter build's data model so the UI logic ports cleanly, but
// every call now hits local SQLite via Tauri IPC (durable, offline, on-disk).
//
// Data model (ported from the iOS/Flutter app + the live brain contract):
//   habit  { id, name, cadence_target (daily|weekly|adhoc), color, created_at }
//   entry  { id, habit_id, logged_on (YYYY-MM-DD), note }  — one check-in per day
// The server brain consumes the DERIVED-SIGNAL payload (AnalysisSignals) computed
// locally in Rust — raw logged_dates[] stay on the device.

import { invoke } from "./tauri-web";

export type Cadence = "daily" | "weekly" | "adhoc";

export interface Habit {
  id: string;
  name: string;
  cadence_target: Cadence;
  /** WHEN it's expected: "daily" | "days:1,3,5" (0=Sun) | "count:3" per week | "adhoc". */
  schedule: string;
  /** "build" = a habit you're forming · "break" = one you're stopping (a mark means "I slipped"). */
  polarity: string;
  /** Empty = active. A timestamp = retired by the customer; the history is kept. */
  archived_at: string;
  color: string;
  created_at: string;
  // joined / computed
  entries_30d: number;
  last_logged_on: string | null;
}

/** The three day-states. `missed` is NEVER stored — it is the ABSENCE of a row on a past day.
 *  Only "done" and "skipped" are records. Added 2026-08-06 with "The Punch Ledger" redesign,
 *  because an entry's mere existence made a deliberate rest day look like a failure. */
export type DayState = "done" | "skipped";

export interface Entry {
  id: string;
  habit_id: string;
  logged_on: string; // YYYY-MM-DD
  note: string | null;
  created_at: string;
  state: DayState;
  /** Optional PROOF PHOTO — a downscaled JPEG data-URL kept ON-DEVICE only, never uploaded.
   *  Empty string = no photo. A new record must never leave this undefined (web build reads it). */
  photo_ref: string;
}

/** WHY a day was missed. A separate record from a completion — see the miss_reasons table. */
export interface MissReason {
  id: string;
  habit_id: string;
  on_day: string;
  reason: string;
  created_at: string;
}

export interface NewHabit {
  name: string;
  cadence_target: Cadence;
  color: string;
  /** WHEN it's expected — see Habit.schedule. Omit and the backend derives it from cadence. */
  schedule?: string;
  polarity?: string;
}

export type HabitPatch = Partial<{
  name: string;
  cadence_target: Cadence;
  color: string;
}>;

export const store = {
  listHabits: () => invoke<Habit[]>("list_habits"),
  getHabit: (id: string) => invoke<Habit | null>("get_habit", { id }),
  createHabit: (input: NewHabit) => invoke<Habit>("create_habit", { input }),
  updateHabit: (id: string, patch: HabitPatch) => invoke<void>("update_habit", { id, patch }),
  deleteHabit: (id: string) => invoke<void>("delete_habit", { id }),

  listEntries: (habitId: string, limit?: number) =>
    invoke<Entry[]>("list_entries", { habitId, limit: limit ?? 400 }),
  // logged_on omitted → server uses caller-supplied local today (passed explicitly).
  // photoRef is TRISTATE: undefined/null = keep any existing photo, "" = clear it, data-URL = set it.
  logEntry: (habitId: string, loggedOn: string, note: string | null, dayState: DayState = "done", photoRef?: string | null) =>
    invoke<Entry>("log_entry", { habitId, loggedOn, note, dayState, photoRef: photoRef ?? null }),
  deleteEntry: (id: string) => invoke<void>("delete_entry", { id }),
  // All logged_on dates for one habit (used to feed the brain + streak math).
  loggedDates: (habitId: string) => invoke<string[]>("logged_dates", { habitId }),
  // All habits with their full logged_dates[] — legacy raw-dates payload (kept for back-compat).
  setArchived: (id: string, archived: boolean) => invoke<Habit>("set_archived", { id, archived }),
  listArchived: () => invoke<Habit[]>("list_archived"),
  setMissReason: (habitId: string, onDay: string, reason: string) =>
    invoke<MissReason>("set_miss_reason", { habitId, onDay, reason }),
  listMissReasons: (limit = 400) => invoke<MissReason[]>("list_miss_reasons", { limit }),
  habitsForAnalysis: () => invoke<AnalysisHabit[]>("habits_for_analysis"),
  // The DERIVED-SIGNAL payload — raw dates stay local, only computed aggregates sent.
  // `today` is the client's local day so month/week boundaries match the UI.
  signalsForAnalysis: (today: string) => invoke<AnalysisSignals>("signals_for_analysis", { today }),
};

// The legacy raw-dates per-habit shape (no longer the primary brain payload).
export interface AnalysisHabit {
  name: string;
  cadence_target: string;
  logged_dates: string[];
}

// ── Derived-signal payload (mirrors src-tauri/src/db.rs AnalysisSignals) ───────
export interface ReasonTally {
  habit: string;
  reason: string;
  count: number;
  of_answered: number;
}

export interface StreakSignal {
  current: number;
  best: number;
  longest_gap_days: number;
  days_since_last: number; // -1 = never logged
  active: boolean;
}
export interface PeriodSignal {
  label: string; // this_month | last_month | last_7d | prev_7d
  checkins: number;
  adherence_pct: number; // -1 = N/A (adhoc cadence)
}
export interface HabitSignals {
  name: string;
  /** "build" or "break" — changes what adherence and streak MEAN. */
  polarity: string;
  cadence_target: string;
  age_days: number;
  maturity: string; // new | establishing | established
  total_checkins: number;
  first_logged_on: string | null;
  last_logged_on: string | null;
  active_days_30d: number;
  adherence_30d_pct: number; // -1 = N/A (adhoc)
  observed_per_week: number;
  cadence_actual: string; // ~daily | ~3x/week | ~weekly | sporadic | dormant
  cadence_mismatch: boolean;
  streak: StreakSignal;
  periods: PeriodSignal[];
  trend: string; // rising | steady | slipping | flat
}
export type FactTone = "good" | "watch" | "neutral";
export interface HeadlineFact {
  kind: string; // streak | consistency | rhythm | cadence | trend | gap | compare | milestone
  text: string; // full observational sentence already carrying figure + unit
  tone: FactTone;
}
export interface AnalysisSignals {
  schema: string; // "habit-signals/v2…"
  generated_on: string;
  habit_count: number;
  total_checkins_all: number;
  active_streaks: number;
  dormant_count: number;
  checkins_last_7d: number;
  checkins_prev_7d: number;
  habits: HabitSignals[];
  headline_facts: HeadlineFact[];
}

// ── Pure streak math (ported from policy/streak.dart) ─────────────────────────
// "today" is the user's local day; a one-day grace means an evening check-in
// won't appear to break the streak just because "today" isn't logged yet.
export function computeStreak(loggedOn: string[], today = todayISO()): number {
  if (loggedOn.length === 0) return 0;
  const set = new Set(loggedOn.map((d) => d.slice(0, 10)));
  let cur = today;
  if (!set.has(cur)) {
    cur = addDays(cur, -1);
    if (!set.has(cur)) return 0;
  }
  let streak = 0;
  while (set.has(cur)) {
    streak++;
    cur = addDays(cur, -1);
  }
  return streak;
}

export function computeBestStreak(loggedOn: string[]): number {
  const days = [...new Set(loggedOn.map((d) => d.slice(0, 10)))].sort();
  if (days.length === 0) return 0;
  let best = 1, run = 1;
  for (let i = 1; i < days.length; i++) {
    const diff = daysBetween(days[i - 1], days[i]);
    if (diff === 1) { run++; if (run > best) best = run; }
    else if (diff === 0) { /* duplicate */ }
    else run = 1;
  }
  return best;
}

// Habit strength — an exponential-moving-average "how strong is this habit right now"
// (0–100), smoother than a binary streak: each on-cadence check-in pushes it toward
// 100, each missed day decays it (~13-day half-life). Normalised per cadence so a
// perfectly-kept weekly habit also reads ~100%. (uhabits-style.)
export function computeStrength(loggedOn: string[], cadence: string = "daily", today = todayISO()): number {
  if (loggedOn.length === 0) return 0;
  const set = new Set(loggedOn.map((d) => d.slice(0, 10)));
  const first = [...set].sort()[0];
  const decay = Math.pow(0.5, 1 / 13);
  const period = cadence === "weekly" ? 7 : cadence === "adhoc" ? 2 : 1;
  const equilibrium = (1 - decay) / (1 - Math.pow(decay, period)); // peak steady-state for on-cadence logging
  const windowStart = addDays(today, -120);
  let d = first < windowStart ? windowStart : first;
  let score = 0;
  while (d <= today) {
    score = score * decay + (set.has(d) ? 1 - decay : 0);
    d = addDays(d, 1);
  }
  return Math.max(0, Math.min(100, Math.round((score / equilibrium) * 100)));
}

export function streakCopy(streak: number): string {
  if (streak === 0) return "Start a streak today";
  if (streak === 1) return "1-day streak — nice start!";
  if (streak < 7) return `${streak}-day streak`;
  if (streak < 30) return `${streak}-day streak · keep it rolling`;
  return `${streak}-day streak · unstoppable`;
}

// ── Date helpers (UTC-anchored to avoid DST drift in arithmetic) ──────────────
export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function pad(n: number): string { return n.toString().padStart(2, "0"); }
function addDays(iso: string, delta: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.slice(0, 10).split("-").map(Number);
  const [by, bm, bd] = b.slice(0, 10).split("-").map(Number);
  const da = Date.UTC(ay, am - 1, ad), db = Date.UTC(by, bm - 1, bd);
  return Math.round((db - da) / 86_400_000);
}

// Last-30-days window (oldest → newest), index 29 = today. For the dot strip.
export function last30Days(today = todayISO()): string[] {
  const out: string[] = [];
  for (let i = 29; i >= 0; i--) out.push(addDays(today, -i));
  return out;
}
